import type { Command, Section } from '../types/command';
import type { CommandRankingStrategy } from '../types/command-strategy';

import { commandScore } from './command-score';

/**
 * Default strategy. A single subsequence pass decides BOTH what shows
 * and the order within a section:
 *
 *   - `commandScore(label, query, aliases)` returns a score in (0, 1].
 *     A score of 0 means the typed characters aren't a subsequence of
 *     the candidate -- not a match -- so the row is dropped. This is
 *     strict (not fuzzy): a letter that isn't present is a hard reject,
 *     so "xtatus" won't match "status" and "variations" won't match
 *     "Integrations". Transpositions and missing letters are still
 *     forgiven (just scored lower).
 *
 * Ordering rules:
 *   - WITHIN a section: `priority` desc, then score desc (best match
 *     first), then insertion order. Priority tiers still pin -- a HIGH
 *     parent stays on top, a LOW "View all" stays at the bottom -- and
 *     score only orders rows inside the same tier.
 *   - EMPTY query: no scoring; rows keep insertion order, so the
 *     "browse" view never reshuffles.
 *   - BETWEEN sections: `max(section.priority, max(commandPriority))`
 *     desc -- the section-priority ladder. A contextual command
 *     registered with `priority: Priority.HIGH` can still lift its
 *     section. Score never moves sections; cross-section order is
 *     stable.
 *
 * So typing reorders rows WITHIN a section by match quality (a match
 * buried in a description sinks to the bottom but is never dropped),
 * while section order and the empty-query browse order stay put. The
 * opt-in relevanceStrategy additionally lets score move sections.
 */

/**
 * The text surfaces a command matches on, beyond its visible label.
 * `keywords` are the consumer-declared hidden aliases (and the place
 * consumers expose server-searchable fields like a flag key);
 * `description` is included so a command the server matched on its
 * description is still kept here -- the server filters by substring,
 * and a substring is always a subsequence, so anything the server
 * returned scores > 0 and is never dropped. Matches buried in the
 * description score low and sort last, which is the intended behaviour.
 */
function searchableAliases(command: Command): string[] {
  const aliases: string[] = [];
  if (command.keywords !== undefined) {
    aliases.push(...command.keywords);
  }
  if (command.description !== undefined && command.description !== '') {
    aliases.push(command.description);
  }
  return aliases;
}

/**
 * Section rank = max(section.priority, max(commandPriority)). Either
 * signal can move a section relative to the `0` spine: a HIGH command
 * (or section) lifts it above, a LOW command AND section sink it
 * below (e.g. Help, pinned to `Priority.LOW` on both the section and
 * every command in it).
 *
 * `maxCommand` is seeded from `-Infinity` (or `0` when the bucket has
 * none) rather than hard-floored at `0` -- the old floor silently
 * swallowed negative priorities, so a section couldn't ever rank below
 * the spine no matter how low its members were.
 */
function sectionRank(section: Section | null, commands: Command[]): number {
  const sectionPriority = section?.priority ?? 0;
  if (commands.length === 0) {
    return sectionPriority;
  }
  let maxCommand = Number.NEGATIVE_INFINITY;
  for (const c of commands) {
    const p = c.priority ?? 0;
    if (p > maxCommand) {
      maxCommand = p;
    }
  }
  return Math.max(sectionPriority, maxCommand);
}

export const priorityFilterStrategy: CommandRankingStrategy = {
  rank(candidates, query) {
    // 1. Filter + score in one pass. `scoreById` drives the
    //    within-section ordering below; it's empty for an empty query
    //    (no scoring -> insertion order preserved).
    const trimmed = query.trim();
    let kept: Command[];
    const scoreById = new Map<string, number>();
    if (trimmed === '') {
      kept = candidates;
    } else {
      // Walk the original candidate array so the kept output preserves
      // registration order; the sort below relies on stable insertion
      // order for ties.
      kept = [];
      for (const command of candidates) {
        const score = commandScore(command.label, trimmed, searchableAliases(command));
        if (score > 0) {
          kept.push(command);
          scoreById.set(command.id, score);
        }
      }
    }

    // 2. Group by section.id (or null for ungrouped) while preserving
    //    insertion order. A Map keeps key-insertion order.
    const buckets = new Map<string | null, { section: Section | null; commands: Command[] }>();
    for (const command of kept) {
      const key = command.section?.id ?? null;
      let bucket = buckets.get(key);
      if (bucket === undefined) {
        bucket = { section: command.section ?? null, commands: [] };
        buckets.set(key, bucket);
      }
      bucket.commands.push(command);
    }

    // 3. Within each bucket: priority desc, then score desc (best match
    //    first), then insertion order. Array.sort is spec-stable since
    //    ES2019, so equal (priority, score) pairs resolve to insertion
    //    order. `scoreById` is empty for an empty query, so every score
    //    reads as 0 and ordering collapses to priority + insertion --
    //    the browse view doesn't reshuffle.
    for (const bucket of buckets.values()) {
      bucket.commands.sort((a, b) => {
        const priorityDelta = (b.priority ?? 0) - (a.priority ?? 0);
        if (priorityDelta !== 0) {
          return priorityDelta;
        }
        return (scoreById.get(b.id) ?? 0) - (scoreById.get(a.id) ?? 0);
      });
    }

    // 4. Order buckets by section rank desc. Section rank is
    //    max(section.priority, max(commandPriority)) -- either signal
    //    can elevate a section. The Map's key-iteration order is our
    //    "insertion order of the first command per section," so
    //    spreading + stable sort gives the right tiebreak.
    const ordered = Array.from(buckets.values(), (bucket) => ({
      ...bucket,
      rank: sectionRank(bucket.section, bucket.commands),
    }));
    ordered.sort((a, b) => b.rank - a.rank);

    return ordered.map(({ rank: _rank, ...rest }) => rest);
  },
};
